import { 
    MaxLengthArray,
    IStorageAccessor
} from 'util/domain';
import SettingsService from 'phoenix/services/settingsService';

const TRACKING_ENABLED_KEY:string = 'sb-tracking-enabled';
const ANALYTICS_DATA:string = 'gaData';


export interface EventDetails {
    category: string;
    action: string;
    label?: string;
    value?: any;
}

export interface IExperimental {
    readonly experimentID:string;
}

export class TrackingService {
    static $inject = [
        '$injector',
        'localStorageService',
        'settingsService'
    ];

    private readonly gaDisableProperty:string;
    private readonly analyticsId = this.settingsService.googleIds.analytics;
    public readonly canOptOut:boolean;

    // Indirectly retrieves references to the $window service to avoid issues of
    // direct window reference on cloned objects which rely on this service.
    private get $window():ng.IWindowService {
        return this.$injector.get('$window');
    }

    private get dataLayer():Array<{}> {
        if(!this.$window.dataLayer) {
            this.$window.dataLayer = new MaxLengthArray();
        }
        return this.$window.dataLayer;
    }

    private get experiments() {
        try {
            return this.$window[ANALYTICS_DATA][this.analyticsId]['experiments'];
        }
        catch {
            return null;
        }
    }
    
    constructor(
        private readonly $injector:ng.auto.IInjectorService,
        private readonly localStorageService:IStorageAccessor,
        private readonly settingsService:SettingsService
    ) { 
        let testKey = 'test-storage-key';

        this.dataLayer.push({
            'gtm.start': new Date().getTime(),
            event: 'gtm.js'
        });

        this.gaDisableProperty = 'ga-disable-' + this.analyticsId;

        // see if any values can be stored; if not, then disable tracking
        this.localStorageService.setStoredValue(testKey, '1');
        this.canOptOut = !!localStorageService.getStoredValue(testKey);
        this.localStorageService.removeStoredValue(testKey);
        
        if (this.canOptOut) {
            let previousVal = this.localStorageService.getStoredValue(TRACKING_ENABLED_KEY);

            // if a value was never set, default to true
            this.enabled = previousVal ? previousVal  === 'true': true;  
        } 
        else {
            this.enabled = false;
        }
    }

    /**
     * Track functionality in the document viewer
     */
    public trackDocumentViewer(eventName: string, eventTargetName: string, documentId: number = null, mimeType: string = '') {
        let eventDetails: any = {
            category: 'DocumentViewer',
            action: eventName,
            label: eventTargetName
        };

        if (documentId !== null) {
            eventDetails.value = { documentId };
        }

        if (mimeType) {
            eventDetails.value = eventDetails.value || { };
            eventDetails.value.mimeType = mimeType;
        }

        this.dataLayer.push({
            event: 'trackableEvent',
            eventDetails
        });
    }
        
    /**
     * Tracks user click with analytics
     */
    public trackClick(eventName: string, eventTargetName = '') {
        let eventDetails:EventDetails = {
            'category': 'userClick',
            'action': eventName,
            'label': eventTargetName
        }

        this.pushAnalyticsEvent('trackableEvent', eventDetails);
    }

    public trackFormSubmission(formName: string, formData:string = '', formValue:number = 1) {
        let eventDetails:EventDetails = {
            'category': 'Form Submission',
            'action': formName,
            'label': formData,
            'value': formValue
        }

        this.pushAnalyticsEvent('trackableEvent', eventDetails);
    }

    public trackPageView(url: string) {
        this.dataLayer.push({
            event: 'stateChangeSuccess',
            routeDetails: {
                route: url,
                title: 'ShareBase by Hyland'
            }
        });
    }

    public addCustomDimension(name, value) {
        this.dataLayer.push({
            dimensions: {
                [name]: value
            }
        });
    }

    public getExperimentVariant(experimentKey:string) {
        let experimentId = this.settingsService.analyticExperimentIds[experimentKey];
        return (!this.experiments || !this.experiments[experimentId]) ? 0 : +this.experiments[experimentId];
    }

    public fireExperimentActivationCheck() {
        this.dataLayer.push({'event': 'optimize.activate'});
    }

    public set enabled(enabled) {
        if(!this.canOptOut){
            enabled = false;
        }

        // https://developers.google.com/analytics/devguides/collection/analyticsjs/user-opt-out
        if(enabled) {
            delete this.$window[this.gaDisableProperty];
        } 
        else {
            this.$window[this.gaDisableProperty] = true;
        }

        this.localStorageService.setStoredValue(TRACKING_ENABLED_KEY, enabled.toString());
    }

    public get enabled() {
        let previouslyEnabled = this.localStorageService.getStoredValue(TRACKING_ENABLED_KEY) === 'true';       
        return previouslyEnabled && this.canOptOut && !this.$window[this.gaDisableProperty]; 
    }

    public pushAnalyticsEvent(eventName:string, eventDetails:EventDetails) {
        this.dataLayer.push({
            'event': eventName,
            'eventDetails': eventDetails
        })
    }
}


// WEBPACK FOOTER //
// ./src/sharebase/services/trackingService/trackingService.ts